import VictronBeam from '@assets/victron-beam.svg?react';
import { PaginationReturn } from '@common/types/service';
import { Flag } from '@components/display/images';
import { useMaxWidthWrapperScrollElement } from '@components/layout/MaxWidthWrapper';
import { FormControlCheckbox } from '@components/utility/checkboxes';
import { Option, Select } from '@components/utility/selects';
import { Tabs } from '@components/utility/tabs';
import { EventListCard } from '@event/components/display/cards/EventListCard';
import {
    useAllPublicEventsQuery,
    useGetBadgesAndLevelsOptionsQuery,
    useGetLanguageSpokenOptionsQuery,
} from '@event/services/events';
import { EventTypes, RegistrationEventListItem } from '@event/types/event';
import { getMarketIcons } from '@event/utils';
import { useAppDispatch } from '@hooks/generic';
import { useAuth } from '@hooks/useAuth';
import { useDebounce } from '@hooks/useDebounce';
import { useInfiniteScroll } from '@hooks/useInfiniteScroll';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import {
    Button,
    CircularProgress,
    Divider,
    Drawer,
    FormControl,
    IconButton,
    InputLabel,
    ListItemText,
    ListSubheader,
    Select as MUISelect,
    MenuItem,
    SelectChangeEvent,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { useGetCountriesQuery } from '@services/countries';
import { useGetMarketsQuery } from '@services/markets';
import { setTitle } from '@store/appSlice';
import { isDateStringValid, isDateValid } from '@utils/common';
import { pathToVP } from '@utils/route';
import clsx from 'clsx';
import { format } from 'date-fns';
import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';

const TAB_CLASSNAME = 'font-inter font-bold text-sm';
const MIN_ITEMS_THRESHOLD = 4;

interface IFilterProps {
    title: string;
    withDivider?: boolean;
}

const allowedColumns = ['date_start'];

const Filter = ({ title, withDivider = true, children }: PropsWithChildren<IFilterProps>) => (
    <div>
        <div className="mt-7 flex flex-col gap-5">
            <p className="font-medium">{title}</p>
            {children}
        </div>
        {withDivider ? <Divider className="!mt-7" /> : null}
    </div>
);

enum ListFilters {
    REGISTERED = 'registered',
    DATE_START = 'date_start',
    COUNTRY = 'country_code',
    MARKETS = 'market_id',
    LANGUAGE = 'language_spoken',
    BADGE = 'badge_id',
    BADGE_LEVEL = 'level_id',
}

const EventList = () => {
    const intl = useIntl();
    const componentContainer = useMaxWidthWrapperScrollElement();

    const SORT_OPTIONS: Record<string, { direction: string; label: string; prefix: string }[]> = useMemo(
        () => ({
            date_start: [
                {
                    direction: 'asc',
                    label: intl.formatMessage({
                        id: 'events.sort.closestToToday',
                        defaultMessage: 'Closest to today',
                        description: '/app/events - Sort option for events closest to today',
                    }),
                    prefix: intl.formatMessage({
                        id: 'common.labels.startDate',
                        defaultMessage: 'Start Date',
                        description: 'Common label for start date',
                    }),
                },
                {
                    direction: 'desc',
                    label: intl.formatMessage({
                        id: 'events.sort.furthestFromToday',
                        defaultMessage: 'Furthest from today',
                        description: '/app/events - Sort option for events furthest from today',
                    }),
                    prefix: intl.formatMessage({
                        id: 'common.labels.startDate',
                        defaultMessage: 'Start Date',
                        description: 'Common label for start date',
                    }),
                },
            ],
        }),
        [intl.locale]
    );

    const [searchParams, setSearchParams] = useSearchParams();
    const [page, setPage] = useState(1);

    const marketIcons = useMemo(() => getMarketIcons(intl), [intl]);

    const listFilters: Record<ListFilters, string[]> = useMemo(
        () =>
            Object.fromEntries(
                Object.values(ListFilters)
                    .map((filter) => {
                        const value = searchParams.get(filter);
                        return value ? [filter, value.includes(',') ? value.split(',') : [value]] : [filter, ''];
                    })
                    .filter((x) => x[1] !== '')
            ),
        [searchParams]
    );
    const filters: Record<'sort' | 'search' | 'tab', string> = useMemo(
        () => ({
            sort: allowedColumns.includes(searchParams.get('sort')?.replace('-', '') as keyof RegistrationEventListItem)
                ? (searchParams.get('sort') as keyof RegistrationEventListItem)
                : 'date_start',
            search: searchParams.get('search') ?? '',
            tab: searchParams.get('tab') ?? '0',
        }),
        [searchParams]
    );
    const [search, setSearch] = useState(filters.search as string);
    const sort = useMemo(
        () => ({
            column: ((filters.sort as string) || undefined)?.replace('-', '') as keyof RegistrationEventListItem,
            direction: ((filters.sort as string) || undefined)?.includes('-') ? 'desc' : ('asc' as 'desc' | 'asc'),
        }),
        [filters.sort]
    );

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const typeFilter = useMemo(() => {
        switch (filters.tab) {
            case '1':
                return `${EventTypes.PUBLIC_ONSITE},${EventTypes.PRIVATE_ONSITE}`.replaceAll('-', '_');
            case '2':
                return `${EventTypes.PUBLIC_VIRTUAL},${EventTypes.PRIVATE_VIRTUAL}`.replaceAll('-', '_');
            case '0':
            default:
                return '';
        }
    }, [filters.tab]);

    const { data: languageOptions, isLoading: areLanguageOptionsLoading } = useGetLanguageSpokenOptionsQuery(
        undefined,
        {
            selectFromResult: (response) => ({
                ...response,
                data: [
                    {
                        label: 'None',
                        value: '',
                    },
                    ...(response.data?.map((language) => ({ label: language.name, value: language.code })) ?? []),
                ],
            }),
        }
    );

    const { data: badgesAndLevels, isLoading: isBadgesAndLevelsLoading } = useGetBadgesAndLevelsOptionsQuery();

    const { data: countries, isLoading: isCountriesLoading } = useGetCountriesQuery();
    const { data: markets, isLoading: isMarketsLoading } = useGetMarketsQuery();

    const {
        data: eventsResponse,
        isFetching,
        isLoading,
        isSuccess,
        isUninitialized,
    } = useAllPublicEventsQuery({
        page_size: 10,
        filters: { ...listFilters, search: [filters.search], type: typeFilter.split(',') },
        page,
        ordering: filters.sort,
    });

    const events = useMemo(() => eventsResponse as PaginationReturn<RegistrationEventListItem>, [eventsResponse]);

    const { user } = useAuth();
    const dispatch = useAppDispatch();

    const handleParamChange = useCallback(
        (field: string, value: string | string[] | null, isDelete?: boolean) => {
            if (isDelete) {
                const values = searchParams.get(field);

                if (values?.split(',').length === 1) {
                    setSearchParams((prev) => {
                        const params = new URLSearchParams(prev);
                        params.delete(field);
                        return params;
                    });
                    setPage(1);
                } else {
                    setSearchParams((prev) => {
                        const params = new URLSearchParams(prev);
                        params.set(
                            field,
                            values
                                ?.split(',')
                                .filter((x) => x !== value)
                                .join(',') ?? ''
                        );
                        return params;
                    });
                    setPage(1);
                }
            } else if (searchParams.get(field) !== value) {
                setSearchParams((prev) => {
                    const params = new URLSearchParams(prev);
                    if (value) {
                        params.set(field, typeof value === 'string' ? value : value.join(','));
                    } else {
                        params.delete(field);
                    }
                    return params;
                });
                setPage(1);
            }
        },
        [searchParams, setSearchParams]
    );

    const theme = useTheme();
    const isLargeOrLarger = useMediaQuery(theme.breakpoints.up('lg'));
    const isFiltered = useMemo(
        () => Object.values(listFilters).flat().filter(Boolean).length > 0 || !!search,
        [listFilters, search]
    );

    const handlePageChange = useCallback((newPage: number) => {
        setPage(newPage);
    }, []);

    const { items, onLoadMore } = useInfiniteScroll({
        scrollElement: componentContainer,
        data: {
            results: events?.results ?? [],
            count: Number(events?.count),
            isFetching,
        },
        shouldPopulate: isSuccess && !!events.results.length,
        pageState: [page, setPage],
    });

    useDebounce({
        value: search,
        delay: 500,
        customHandler: (value) => handleParamChange('search', value),
        trigger: () => (searchParams.get('search') ?? '') !== search,
    });

    useEffect(() => {
        setSearch(filters.search as string);
    }, [filters.search, setSearchParams]);

    const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { target } = e;
        setSearch(target.value);
    };

    const onDateChange = useCallback(
        (date: Date | null, name: string) => {
            handleParamChange(name, date && isDateValid(date) ? format(date, 'yyyy-MM-dd') : '');
        },
        [handleParamChange]
    );

    const onSelectChange = useCallback(
        (event: SelectChangeEvent<string[]>) => {
            const {
                target: { value, name },
            } = event;
            handleParamChange(name, Array.isArray(value) ? value.join(',') : value);
        },
        [handleParamChange]
    );

    const onCheckboxChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const { name, value } = e.target;
            const searchParamValue = searchParams.get(name);
            const newValue = searchParamValue !== value ? value : '';
            handleParamChange(name, newValue);
        },
        [handleParamChange, searchParams]
    );

    useEffect(() => {
        dispatch(setTitle('Event List'));
    }, [dispatch]);

    const resetFilters = useCallback(() => {
        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);
            Object.values(ListFilters).forEach((filter) => params.delete(filter));
            params.delete('search');
            return params;
        });
        handlePageChange(1);
    }, [handlePageChange, setSearchParams]);

    const renderLanguageOption = useCallback(
        ({ label, value }: Option) => (
            <ListItemText
                slotProps={{
                    primary: {
                        className: 'flex items-center gap-2',
                    },
                }}
            >
                <Flag flag={value == '' ? 'victron' : (value as string)} />
                <Typography>{label}</Typography>
            </ListItemText>
        ),
        []
    );

    const Filters = useMemo(
        () => (
            <>
                <div className="flex justify-between items-center">
                    <p className="font-bold text-lg">
                        <FormattedMessage
                            id="event.list.filters"
                            defaultMessage="Filters"
                            description="Title for the filter section in the website event list"
                        />
                    </p>
                    <a className="font-medium text-sm text-spanish-blue cursor-pointer" onClick={resetFilters}>
                        <FormattedMessage
                            id="common.actions.clearFilters"
                            defaultMessage="Clear filters"
                            description="Common action to clear all applied filters"
                        />
                    </a>
                </div>
                <Filter
                    title={intl.formatMessage({ id: 'events.filters.registered', defaultMessage: 'Registered' })}
                    withDivider={isLargeOrLarger}
                >
                    <FormControlCheckbox
                        name="registered"
                        labelProps={{
                            value: true,
                            label: intl.formatMessage({ id: 'common.labels.yes', defaultMessage: 'Yes' }),
                            className: '!ml-0 gap-2',
                        }}
                        className="!p-0"
                        onChange={onCheckboxChange}
                        checked={listFilters.registered?.[0] === 'true'}
                    />
                    <FormControlCheckbox
                        name="registered"
                        labelProps={{
                            value: false,
                            label: intl.formatMessage({ id: 'common.labels.no', defaultMessage: 'No' }),
                            className: '!ml-0 gap-2',
                        }}
                        className="!p-0"
                        onChange={onCheckboxChange}
                        checked={listFilters.registered?.[0] === 'false'}
                    />
                </Filter>
                <Filter
                    title={intl.formatMessage({ id: 'events.filters.name', defaultMessage: 'Name' })}
                    withDivider={isLargeOrLarger}
                >
                    <TextField
                        name="name"
                        size="small"
                        onChange={onInputChange}
                        value={search}
                        slotProps={{
                            input: {
                                endAdornment: search ? (
                                    <IconButton edge="end" size="small" onClick={() => setSearch('')}>
                                        <ClearIcon />
                                    </IconButton>
                                ) : null,
                            },
                        }}
                    />
                </Filter>
                <Filter
                    title={intl.formatMessage({
                        id: 'events.filters.startingAfterDate',
                        defaultMessage: 'Starting After Date',
                    })}
                    withDivider={isLargeOrLarger}
                >
                    <DatePicker
                        format="dd-MM-yyyy"
                        slotProps={{
                            field: {
                                clearable: true,
                            },
                            textField: {
                                size: 'small',
                            },
                        }}
                        onChange={(value) => onDateChange(value, 'date_start')}
                        value={
                            listFilters.date_start && isDateStringValid(listFilters.date_start[0])
                                ? new Date(listFilters.date_start[0])
                                : null
                        }
                    />
                </Filter>
                <Filter
                    title={intl.formatMessage({ id: 'events.filters.country', defaultMessage: 'Country' })}
                    withDivider={isLargeOrLarger}
                >
                    <Select
                        searchable
                        multiple
                        name="country_code"
                        size="small"
                        loading={isCountriesLoading}
                        items={countries ?? []}
                        fcSX={{ size: 'small' }}
                        renderValue={(selected) => (
                            <div>
                                {(selected as [])?.length}{' '}
                                {intl.formatMessage({ id: 'common.labels.selected', defaultMessage: 'selected' })}
                            </div>
                        )}
                        value={listFilters.country_code ?? []}
                        onChange={(e) => onSelectChange(e as SelectChangeEvent<string[]>)}
                        virtualize
                    />
                </Filter>
                <Filter
                    title={intl.formatMessage({ id: 'events.filters.language', defaultMessage: 'Language' })}
                    withDivider={isLargeOrLarger}
                >
                    <Select
                        searchable
                        multiple
                        name="language_spoken"
                        size="small"
                        items={languageOptions ?? []}
                        loading={areLanguageOptionsLoading}
                        fcSX={{ size: 'small' }}
                        renderValue={(selected) => (
                            <div>
                                {(selected as [])?.length}{' '}
                                {intl.formatMessage({ id: 'common.labels.selected', defaultMessage: 'selected' })}
                            </div>
                        )}
                        value={listFilters.language_spoken ?? []}
                        onChange={(e) => onSelectChange(e as SelectChangeEvent<string[]>)}
                        renderOption={renderLanguageOption}
                        virtualize
                    />
                </Filter>
                <Filter
                    title={intl.formatMessage({ id: 'events.filters.badgeLevel', defaultMessage: 'Badge Level' })}
                    withDivider={isLargeOrLarger}
                >
                    <Select
                        searchable
                        searchProps={{
                            autoFocus: false,
                        }}
                        multiple
                        loading={isBadgesAndLevelsLoading}
                        name="level_id"
                        size="small"
                        items={badgesAndLevels?.levels ?? []}
                        fcSX={{ size: 'small' }}
                        renderValue={(selected) => (
                            <div>
                                {(selected as [])?.length}{' '}
                                {intl.formatMessage({ id: 'common.labels.selected', defaultMessage: 'selected' })}
                            </div>
                        )}
                        value={listFilters.level_id ?? []}
                        onChange={(e) => onSelectChange(e as SelectChangeEvent<string[]>)}
                    />
                </Filter>
                <Filter
                    title={intl.formatMessage({ id: 'events.filters.badgeSolution', defaultMessage: 'Badge Solution' })}
                    withDivider={isLargeOrLarger}
                >
                    <Select
                        searchable
                        searchProps={{
                            autoFocus: false,
                        }}
                        multiple
                        loading={isBadgesAndLevelsLoading}
                        name="badge_id"
                        size="small"
                        items={badgesAndLevels?.badges ?? []}
                        fcSX={{ size: 'small' }}
                        renderValue={(selected) => (
                            <div>
                                {(selected as [])?.length}{' '}
                                {intl.formatMessage({ id: 'common.labels.selected', defaultMessage: 'selected' })}
                            </div>
                        )}
                        value={listFilters.badge_id ?? []}
                        onChange={(e) => onSelectChange(e as SelectChangeEvent<string[]>)}
                    />
                </Filter>
                <Filter
                    title={intl.formatMessage({ id: 'events.filters.markets', defaultMessage: 'Markets' })}
                    withDivider={isLargeOrLarger}
                >
                    <Select
                        searchable
                        multiple
                        name="market_id"
                        size="small"
                        loading={isMarketsLoading}
                        items={
                            markets?.map((x) => ({
                                label: x.title,
                                value: x.id.toString(),
                                icon: marketIcons[x.slug]?.icon ?? VictronBeam,
                            })) ?? []
                        }
                        fcSX={{ size: 'small' }}
                        renderValue={(selected) => (
                            <div>
                                {(selected as [])?.length}{' '}
                                {intl.formatMessage({ id: 'common.labels.selected', defaultMessage: 'selected' })}
                            </div>
                        )}
                        value={listFilters.market_id ?? []}
                        onChange={(e) => onSelectChange(e as SelectChangeEvent<string[]>)}
                    />
                </Filter>
            </>
        ),
        [
            countries,
            isLargeOrLarger,
            listFilters.country_code,
            listFilters.date_start,
            listFilters.language_spoken,
            listFilters.market_id,
            listFilters.level_id,
            listFilters.badge_id,
            listFilters.registered,
            markets,
            badgesAndLevels,
            onCheckboxChange,
            onDateChange,
            onSelectChange,
            renderLanguageOption,
            resetFilters,
            search,
        ]
    );

    return (
        <div className="md:px-16 py-11 font-inter h-full">
            <div className="h-full">
                <Tabs
                    className="gap-5"
                    value={Number(filters.tab)}
                    onChange={(_, newValue) => handleParamChange('tab', `${newValue}`)}
                >
                    <Tabs.Tab
                        label={
                            <p className={TAB_CLASSNAME}>
                                <FormattedMessage id="events.tabs.all" defaultMessage="All" />
                            </p>
                        }
                    />
                    <Tabs.Tab
                        label={
                            <p className={TAB_CLASSNAME}>
                                <FormattedMessage id="events.tabs.onsite" defaultMessage="Onsite" />
                            </p>
                        }
                    />
                    <Tabs.Tab
                        label={
                            <p className={TAB_CLASSNAME}>
                                <FormattedMessage id="events.tabs.virtual" defaultMessage="Virtual" />
                            </p>
                        }
                    />
                    <Tabs.Tab
                        label={
                            <a
                                href={pathToVP('/learn/event-videos').toString()}
                                className={TAB_CLASSNAME}
                                onClick={(event) => event.stopPropagation()}
                            >
                                <FormattedMessage
                                    id="events.actions.pastEventVideos"
                                    defaultMessage="Past Event Videos"
                                />
                            </a>
                        }
                    />
                </Tabs>
                <Drawer open={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} className="lg:!hidden">
                    <IconButton className="!absolute right-0" size="small" onClick={() => setIsDrawerOpen(false)}>
                        <CloseIcon fontSize="small" color="black" />
                    </IconButton>
                    <div className="pt-10 pb-5 px-3">{Filters}</div>
                </Drawer>
                <div className="bg-white px-6 py-4 lg:py-14 flex gap-11 items-stretch">
                    <div className="min-w-[250px] hidden lg:block">{Filters}</div>
                    <div className="w-full flex flex-col relative">
                        <div className="flex flex-col lg:flex-row items-end lg:items-baseline justify-between mb-12 gap-5">
                            <div className="lg:!hidden !absolute -top-1 left-0 flex items-center gap-2">
                                <IconButton
                                    className="!rounded-md "
                                    onClick={() => setIsDrawerOpen(true)}
                                    sx={{ backgroundColor: '#F2F4F7' }}
                                >
                                    <FilterAltOutlinedIcon fontSize="medium" color="black" />
                                </IconButton>
                                {isFiltered ? (
                                    <p
                                        className="text-sm bg-spanish-blue text-white px-2 py-0.5 rounded cursor-pointer"
                                        onClick={() => setIsDrawerOpen(true)}
                                    >
                                        <FormattedMessage
                                            id="common.labels.filtered"
                                            defaultMessage="Filtered"
                                            description="Common indicator showing filters are active"
                                        />
                                    </p>
                                ) : null}
                            </div>
                            <p>
                                <span className="text-lg lg:text-4xl font-bold text-spanish-blue">{events?.count}</span>{' '}
                                <span className="font-semibold text-sm lg:ml-3">
                                    <FormattedMessage id="events.messages.eventsFound" defaultMessage="Events found" />
                                </span>
                            </p>
                            <FormControl className="w-full lg:w-[310px]">
                                <InputLabel htmlFor="sort-select">
                                    <FormattedMessage
                                        id="common.actions.sort"
                                        defaultMessage="Sort"
                                        description="Common label for sort functionality"
                                    />
                                </InputLabel>
                                <MUISelect
                                    defaultValue={'date_start/asc'}
                                    id="sort-select"
                                    label={intl.formatMessage({
                                        id: 'common.actions.sort',
                                        defaultMessage: 'Sort',
                                        description: 'Common label for sort functionality',
                                    })}
                                    size="small"
                                    onChange={(e) => {
                                        const [column, direction] = e.target.value.split('/');
                                        handleParamChange('sort', `${direction === 'asc' ? '' : '-'}${column}`);
                                    }}
                                    SelectDisplayProps={{
                                        className: 'flex gap-2 items-center',
                                    }}
                                    value={`${sort.column}/${sort.direction}`}
                                    renderValue={(value: string) => {
                                        const [column, direction] = value.split('/');
                                        const option = SORT_OPTIONS[column].find(
                                            (sort) => sort.direction === direction
                                        );
                                        return (
                                            <div className="flex items-center w-full gap-1 h-full">
                                                <div>
                                                    {option?.direction === 'asc' ? (
                                                        <ArrowUpwardIcon />
                                                    ) : (
                                                        <ArrowDownwardIcon />
                                                    )}
                                                </div>
                                                <p className="text-center flex-grow truncate">
                                                    {option?.prefix}: {option?.label}
                                                </p>
                                            </div>
                                        );
                                    }}
                                >
                                    {Object.keys(SORT_OPTIONS).map((field) => [
                                        <ListSubheader key={`sort-header-${field}`}>
                                            {SORT_OPTIONS[field][0].prefix}
                                        </ListSubheader>,
                                        ...SORT_OPTIONS[field].map((option) => (
                                            <MenuItem
                                                key={`sort-${field}-${option.direction}`}
                                                className="flex gap-8 justify-start items-center"
                                                value={`${field}/${option.direction}`}
                                            >
                                                <div>
                                                    {option.direction === 'asc' ? (
                                                        <ArrowUpwardIcon />
                                                    ) : (
                                                        <ArrowDownwardIcon />
                                                    )}
                                                </div>
                                                <p>{option.label}</p>
                                            </MenuItem>
                                        )),
                                    ])}
                                </MUISelect>
                            </FormControl>
                        </div>
                        <CircularProgress
                            className={clsx('m-auto', { 'mb-10': !!events?.count })}
                            sx={{
                                display:
                                    (isFetching || isLoading) && items.length > MIN_ITEMS_THRESHOLD ? 'block' : 'none',
                            }}
                        />
                        {events?.count ? (
                            <div className="flex flex-col gap-2.5 h-full relative">
                                {items.map((event) => (
                                    <EventListCard
                                        key={`event-${event.type}-${event.id}`}
                                        event={event}
                                        markets={markets}
                                        user={user}
                                    />
                                ))}
                            </div>
                        ) : null}
                        {!isFetching && !isLoading && !isUninitialized && !events?.count ? (
                            <div className="mb-10 m-auto w-fit lg:absolute lg:top-1/2 lg:left-1/2 lg:translate-x-[-50%] lg:translate-y-[-50%]">
                                <p className="bg-charleston-green-10 p-4 rounded font-medium text-base text-dark-jungle-green">
                                    <FormattedMessage
                                        id="events.messages.noEventsAvailable"
                                        defaultMessage="No events available. This may be due to your current role or because no events have been scheduled."
                                    />
                                </p>
                            </div>
                        ) : null}
                        <CircularProgress
                            className={clsx('m-auto', { 'mt-10': !!events?.count })}
                            sx={{
                                display: isFetching || isLoading ? 'block' : 'none',
                            }}
                        />
                        {events?.count > items.length && !isFetching && !isLoading ? (
                            <Button
                                variant="text"
                                sx={{
                                    boxShadow: '0px 1px 3px 0px #A6AFC366',
                                    color: '#111928',
                                    padding: '12px 24px',
                                    fontWeight: 500,
                                    fontFamily: 'Inter',
                                    fontSize: 16,
                                    gap: 1,
                                    mt: '43px',
                                    mx: 'auto',
                                    '&:hover': {
                                        boxShadow: '0px 1px 3px 0px #A6AFC366',
                                    },
                                }}
                                color="secondary-alt"
                                onClick={onLoadMore}
                                className="w-full lg:w-auto"
                            >
                                <AddCircleOutlineIcon />
                                <FormattedMessage
                                    id="events.actions.loadMoreEvents"
                                    defaultMessage="Load more events"
                                />
                            </Button>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
};

export { EventList };
